import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import {
  CircleF,
  GoogleMap,
  GoogleMarkerClusterer,
  InfoWindowF,
  MarkerF,
  RectangleF,
  useJsApiLoader,
} from "@react-google-maps/api";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import MapIcon from "@mui/icons-material/Map";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { PageHeader, PageShell } from "../../components/UI/Page";
import { EmptyState, LoadingState } from "../../components/UI/States";
import { storeCoords } from "../../components/WebLeads/utilities";
import { selectUser } from "../../features/userSlice";
import { selectRoles } from "../../features/roleSlice";
import { useIsDarkMode } from "../../theme/ThemeContext";
import {
  MAPS_LOADER_OPTIONS,
  MAP_STYLE_DARK,
  MAP_STYLE_LIGHT,
} from "../../utils/googleMaps";
import {
  BRANCHES,
  derivePermissions,
  fetchLeads,
  leadBranch,
  monthsAgo,
  titleCase,
  wasContacted,
} from "./utilities";

// Web lead pin map. Two consumers:
//   /webleads/webleadmap        — signed in, full filter/search/detail UI
//   /external/webleads/:key     — key-protected wall display, no user object
// `display` drives the second: no controls, bigger pins, auto-refresh.

// Path data from @mui/icons-material/Store (24x24 viewBox). Copied rather than
// imported because Google Maps wants raw path notation, and MUI wraps its
// icons in a React component with no way to read the path back out.
const STORE_ICON_PATH =
  "M20 4H4v2h16zm1 10v-2l-1-5H4l-1 5v2h1v6h10v-6h4v6h2v-6zm-9 4H6v-4h6z";

const DEFAULT_CENTER = { lat: 41.7534, lng: -86.1108 };
const FILTER_KEY = "webleads.map.filters";
const TV_REFRESH_MS = 5 * 60 * 1000;

// Density view. Google removed HeatmapLayer from the Maps JS API (deprecated
// May 2025, gone May 2026) and points at deck.gl, which is a lot of bundle for
// one view — so this bins leads into a grid and shades each cell instead. It
// also beats a blurred heatmap on this data: cells carry a readable count.
//
// Sequential ramp: one hue, light -> dark. Magnitude, not identity.
const DENSITY_RAMP = ["#bae6fd", "#7dd3fc", "#38bdf8", "#0284c7", "#075985"];

// Degrees per cell at a given zoom — roughly halving as you zoom in, so the
// grid stays about the same size on screen.
const binSizeForZoom = (zoom) => {
  if (zoom <= 6) return 0.5;
  if (zoom === 7) return 0.25;
  if (zoom === 8) return 0.2;
  if (zoom === 9) return 0.1;
  if (zoom === 10) return 0.05;
  if (zoom === 11) return 0.025;
  return 0.0125;
};

// Pin state is the question a manager actually asks of a map — who is waiting,
// who is being worked, who bought. Category lives in the filters instead.
const PIN_STATES = {
  awaiting: { label: "Awaiting contact", color: "#dc2626" },
  working: { label: "Working", color: "#0284c7" },
  quoted: { label: "Quoted", color: "#8b5cf6" },
  won: { label: "Won", color: "#059669" },
  closed: { label: "Closed, no sale", color: "#64748b" },
  deleted: { label: "Deleted", color: "#94a3b8" },
};

const STATE_ORDER = ["awaiting", "working", "quoted", "won", "closed", "deleted"];
const DEFAULT_STATES = ["awaiting", "working", "quoted", "won"];

// Values the public form offers for "what are you working on".
const CATEGORIES = [
  "Lumber / Building Materials",
  "Deck Materials",
  "Insulation",
  "Doors / Windows",
  "Siding / Roofing",
  "Cabinetry",
  "Post Frame Buildings",
  "Trusses / Panels",
];

const RANGES = [
  { months: 1, label: "30 days" },
  { months: 3, label: "3 mo" },
  { months: 6, label: "6 mo" },
  { months: 12, label: "12 mo" },
];

const RADII = [
  { miles: 10, label: "10 mi" },
  { miles: 20, label: "20 mi" },
  { miles: 30, label: "30 mi" },
];

const MILES_TO_METERS = 1609.34;

// Slugs storeCoords keys back to BRANCHES names: "Fort Wayne - Store" ->
// "fort_wayne", "Three Oaks" -> "three_oaks".
const storeKey = (branch) =>
  branch
    .toLowerCase()
    .replace(/\s*-\s*store$/, "")
    .replace(/\s+/g, "_");

const STORES = BRANCHES.map((branch) => ({
  branch,
  coords: storeCoords[storeKey(branch)],
})).filter((store) => store.coords);

// ------------------------------------------------------------- lead helpers

const pinState = (lead) => {
  if (lead.leadProgress?.leadStatus?.deleted?.status) return "deleted";
  const isCompleted = Boolean(lead.leadProgress?.leadStatus?.completed?.status);
  if (lead.saleData?.orderNumber) return "won";
  if (isCompleted) return "closed";
  if (!wasContacted(lead)) return "awaiting";
  if (lead.saleData?.quoteNumber) return "quoted";
  return "working";
};

const coordsOf = (lead) => {
  const lat = parseFloat(lead.locationData?.lat);
  const lng = parseFloat(lead.locationData?.lon);
  if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
  return { lat, lng };
};

const fullAddress = (entry = {}) =>
  [
    entry.customerAddress,
    entry.customerCity,
    entry.customerState,
    entry.customerZip,
  ]
    .filter(Boolean)
    .join(", ");

const loadFilters = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(FILTER_KEY));
    if (!saved || typeof saved !== "object") return null;
    return saved;
  } catch {
    return null;
  }
};

// --------------------------------------------------------------- fragments

const Chip = ({ label, active, onClick, dot, title }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    aria-pressed={active}
    className={`inline-flex min-h-[32px] items-center gap-1.5 rounded-full border px-3 text-xs font-semibold transition-colors ${
      active
        ? "border-sky-500/40 bg-sky-500/15 text-sky-700 dark:text-sky-300"
        : "app-surface app-muted hover:text-[color:var(--app-text)]"
    }`}
  >
    {dot && (
      <span
        className="h-2.5 w-2.5 shrink-0 rounded-full ring-1 ring-white/70 dark:ring-slate-900/70"
        style={{ backgroundColor: dot }}
      />
    )}
    {label}
  </button>
);

// Same look as the MUI ToggleButtonGroup segmented control in the theme.
const SegmentedControl = ({ options, value, onChange, label }) => (
  <div
    role="group"
    aria-label={label}
    className="inline-flex items-center gap-0.5 rounded-xl bg-[color:var(--app-hover)] p-[3px]"
  >
    {options.map((option) => (
      <button
        type="button"
        key={option.value}
        onClick={() => onChange(option.value)}
        aria-pressed={value === option.value}
        className={`min-h-[32px] rounded-lg px-3 text-xs font-semibold transition-colors ${
          value === option.value
            ? "bg-white text-[color:var(--app-text)] shadow-sm dark:bg-white/[0.14] dark:shadow-none"
            : "app-muted hover:text-[color:var(--app-text)]"
        }`}
      >
        {option.label}
      </button>
    ))}
  </div>
);

const Legend = ({ counts, activeStates, onToggle, compact }) => (
  <div className={`flex flex-wrap items-center gap-1.5 ${compact ? "" : "gap-2"}`}>
    {STATE_ORDER.map((key) => {
      const state = PIN_STATES[key];
      const count = counts[key] || 0;
      if (compact) {
        return (
          <span
            key={key}
            className="app-card inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold shadow-sm"
          >
            <span
              className="h-2.5 w-2.5 rounded-full"
              style={{ backgroundColor: state.color }}
            />
            {state.label}
            <span className="font-extrabold">{count}</span>
          </span>
        );
      }
      return (
        <Chip
          key={key}
          label={`${state.label} · ${count}`}
          dot={state.color}
          active={activeStates.includes(key)}
          onClick={() => onToggle(key)}
          title={
            activeStates.includes(key)
              ? `Hide ${state.label.toLowerCase()} pins`
              : `Show ${state.label.toLowerCase()} pins`
          }
        />
      );
    })}
  </div>
);

// ------------------------------------------------------------------- page

const LeadMap = ({ display = false }) => {
  const { isLoaded, loadError } = useJsApiLoader(MAPS_LOADER_OPTIONS);

  const user = useSelector(selectUser);
  const rolesState = useSelector(selectRoles);
  const isDark = useIsDarkMode();

  const roleList = useMemo(
    () => rolesState?.roles || user?.roles || [],
    [rolesState, user]
  );
  // The wall display has no signed-in user; it always shows the whole company.
  const permissions = useMemo(
    () => derivePermissions(display ? [] : roleList, display ? {} : user || {}),
    [roleList, user, display]
  );
  const branchOptions = display
    ? BRANCHES
    : permissions.seesAllLocations
    ? BRANCHES
    : permissions.viewableBranches;
  const canPickAnyBranch = display || permissions.seesAllLocations;

  const saved = useMemo(() => (display ? null : loadFilters()), [display]);

  const [leads, setLeads] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loadedAt, setLoadedAt] = useState(null);

  const [months, setMonths] = useState(saved?.months || (display ? 3 : 6));
  const [states, setStates] = useState(
    saved?.states?.length ? saved.states : DEFAULT_STATES
  );
  const [categories, setCategories] = useState(saved?.categories || []);
  const [branch, setBranch] = useState(saved?.branch || "All Locations");
  // "heat" is the pre-removal HeatmapLayer value; anything unrecognised falls
  // back to pins rather than rendering nothing.
  const [view, setView] = useState(
    saved?.view === "density" ? "density" : "pins"
  );
  const [radiusMiles, setRadiusMiles] = useState(saved?.radiusMiles || 20);

  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [selectedBin, setSelectedBin] = useState(null);
  const [openStore, setOpenStore] = useState(null);
  const [zoom, setZoom] = useState(8);

  const mapRef = useRef(null);
  // Only the density grid cares about zoom. Tracking it in state during a pin
  // zoom would re-render every marker for nothing, so the handler reads the
  // live view off a ref and stays quiet in pin mode.
  const viewRef = useRef(view);
  viewRef.current = view;

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
    setZoom(map.getZoom() || 8);
  }, []);
  const onMapUnmount = useCallback(() => {
    mapRef.current = null;
  }, []);
  const onZoomChanged = useCallback(() => {
    if (mapRef.current && viewRef.current === "density")
      setZoom(mapRef.current.getZoom() || 8);
  }, []);

  // ------------------------------------------------------------- data load

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Pull a fixed 12 months of history once; the range chips narrow what is
      // already in memory rather than triggering another round trip.
      const since = monthsAgo(12);
      const [active, completed, deleted] = await Promise.all([
        fetchLeads("Active", null),
        fetchLeads("Completed", null, { since }),
        fetchLeads("Deleted", null, { since }),
      ]);
      setLeads([...active, ...completed, ...deleted]);
      setLoadedAt(new Date());
    } catch (err) {
      console.error("LeadMap load:", err);
      setError("Could not load web leads. Check your connection and retry.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Wall display refreshes itself — nobody is there to hit the button.
  useEffect(() => {
    if (!display) return undefined;
    const timer = setInterval(load, TV_REFRESH_MS);
    return () => clearInterval(timer);
  }, [display, load]);

  useEffect(() => {
    if (display) return;
    localStorage.setItem(
      FILTER_KEY,
      JSON.stringify({ months, states, categories, branch, view, radiusMiles })
    );
  }, [display, months, states, categories, branch, view, radiusMiles]);

  // -------------------------------------------------------------- filtering

  // Pins need coordinates, and only leads that reached a manager get geocoded
  // — the gap is reported rather than silently dropped.
  const scoped = useMemo(() => {
    if (!leads) return [];
    const start = monthsAgo(months).getTime();
    const allowedBranches =
      branch !== "All Locations"
        ? [branch]
        : canPickAnyBranch
        ? null
        : permissions.viewableBranches;
    return leads
      .filter((lead) => {
        const entered = new Date(lead.webleadEntry?.entryDate).getTime();
        if (Number.isNaN(entered) || entered < start) return false;
        if (!allowedBranches) return true;
        return allowedBranches.includes(leadBranch(lead));
      })
      .map((lead) => ({ lead, state: pinState(lead), coords: coordsOf(lead) }));
  }, [leads, months, branch, canPickAnyBranch, permissions.viewableBranches]);

  const stateCounts = useMemo(() => {
    const counts = {};
    scoped.forEach((row) => {
      counts[row.state] = (counts[row.state] || 0) + 1;
    });
    return counts;
  }, [scoped]);

  const matchesQuery = useCallback(
    (lead) => {
      if (!query.trim()) return true;
      const q = query.trim().toLowerCase();
      const entry = lead.webleadEntry || {};
      return [
        entry.customerName,
        entry.customerEmail,
        entry.customerPhone,
        fullAddress(entry),
      ].some((value) => value && String(value).toLowerCase().includes(q));
    },
    [query]
  );

  const visible = useMemo(
    () =>
      scoped.filter(
        (row) =>
          states.includes(row.state) &&
          (categories.length === 0 ||
            categories.includes(row.lead.webleadEntry?.customerWorkingOn)) &&
          matchesQuery(row.lead)
      ),
    [scoped, states, categories, matchesQuery]
  );

  const plotted = useMemo(() => visible.filter((row) => row.coords), [visible]);
  const unmapped = visible.length - plotted.length;

  const searchResults = useMemo(
    () => (query.trim() ? plotted.slice(0, 8) : []),
    [query, plotted]
  );

  // Grid-bin density: snap every plotted lead to a cell, then shade the cell
  // by how many landed in it. Recomputed on zoom so the grid keeps its
  // on-screen size.
  const density = useMemo(() => {
    if (view !== "density") return { cells: [], max: 0 };
    const size = binSizeForZoom(zoom);
    const cells = new Map();
    plotted.forEach((row) => {
      const south = Math.floor(row.coords.lat / size) * size;
      const west = Math.floor(row.coords.lng / size) * size;
      const key = `${south.toFixed(4)}:${west.toFixed(4)}`;
      const cell = cells.get(key) || {
        key,
        count: 0,
        bounds: {
          south,
          west,
          north: south + size,
          east: west + size,
        },
      };
      cell.count += 1;
      cells.set(key, cell);
    });
    const list = [...cells.values()];
    return {
      cells: list,
      max: list.reduce((top, cell) => Math.max(top, cell.count), 0),
    };
  }, [view, zoom, plotted]);

  const densityColor = useCallback(
    (count) => {
      if (!density.max) return DENSITY_RAMP[0];
      const step = Math.ceil((count / density.max) * DENSITY_RAMP.length);
      return DENSITY_RAMP[Math.min(step, DENSITY_RAMP.length) - 1];
    },
    [density.max]
  );

  // ---------------------------------------------------------------- markers

  const pinIcon = useCallback(
    (state) => ({
      path: window.google.maps.SymbolPath.CIRCLE,
      fillColor: PIN_STATES[state].color,
      fillOpacity: 1,
      // A 2px ring keeps overlapping pins readable on any basemap.
      strokeColor: isDark ? "#0f172a" : "#ffffff",
      strokeWeight: 2,
      scale: display ? 9 : 7,
    }),
    [isDark, display]
  );

  // Google Maps symbols take raw SVG path notation, so the MUI icon renders as
  // a vector — crisp at any zoom, recolorable, and no image request.
  const storeIcon = useCallback(
    (open) => ({
      path: STORE_ICON_PATH,
      fillColor: open ? "#0284c7" : isDark ? "#e2e8f0" : "#334155",
      fillOpacity: 1,
      strokeColor: isDark ? "#0f172a" : "#ffffff",
      strokeWeight: 1.5,
      scale: display ? 1.4 : 1.1,
      // MUI icons are drawn in a 24x24 box; center it on the store.
      anchor: new window.google.maps.Point(12, 12),
    }),
    [isDark, display]
  );

  const clusterOptions = useMemo(
    () => ({
      renderer: {
        render: ({ count, position }) => {
          const size = count < 10 ? 38 : count < 50 ? 46 : 56;
          const svg = window.btoa(
            `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
              <circle cx="50" cy="50" r="46" fill="#0284c7" opacity="0.25"/>
              <circle cx="50" cy="50" r="34" fill="#0284c7" opacity="0.9"/>
            </svg>`
          );
          return new window.google.maps.Marker({
            position,
            icon: {
              url: `data:image/svg+xml;base64,${svg}`,
              scaledSize: new window.google.maps.Size(size, size),
            },
            label: {
              text: String(count),
              color: "#ffffff",
              fontSize: "12px",
              fontWeight: "700",
            },
            zIndex: 1000 + count,
          });
        },
      },
    }),
    []
  );

  const focusLead = (row) => {
    setSelected(row);
    if (mapRef.current) {
      mapRef.current.panTo(row.coords);
      if ((mapRef.current.getZoom() || 8) < 11) mapRef.current.setZoom(12);
    }
  };

  const toggleIn = (list, setList, value) =>
    setList(
      list.includes(value)
        ? list.filter((item) => item !== value)
        : [...list, value]
    );

  const resetFilters = () => {
    setStates(DEFAULT_STATES);
    setCategories([]);
    setBranch("All Locations");
    setMonths(6);
    setQuery("");
  };

  // ----------------------------------------------------------------- render

  if (loadError) {
    return (
      <div className="app-canvas flex min-h-[60vh] items-center justify-center px-4">
        <div className="app-card w-full max-w-md rounded-2xl border">
          <EmptyState
            icon={ErrorOutlineIcon}
            title="Google Maps failed to load."
            description="Check the API key and the site's referrer restrictions."
          />
        </div>
      </div>
    );
  }

  if (!isLoaded || (loading && !leads)) {
    return <LoadingState label="Loading the lead map…" />;
  }

  const mapOptions = {
    styles: isDark ? MAP_STYLE_DARK : MAP_STYLE_LIGHT,
    mapTypeControl: false,
    streetViewControl: false,
    fullscreenControl: !display,
    zoomControl: !display,
    gestureHandling: display ? "none" : "greedy",
    clickableIcons: false,
  };

  const mapHeight = display ? "100vh" : "min(72vh, 780px)";

  const mapCanvas = (
    <GoogleMap
      mapContainerStyle={{ width: "100%", height: mapHeight }}
      center={DEFAULT_CENTER}
      zoom={8}
      options={mapOptions}
      onLoad={onMapLoad}
      onUnmount={onMapUnmount}
      onZoomChanged={onZoomChanged}
      onClick={() => {
        setSelected(null);
        setSelectedBin(null);
      }}
    >
      {STORES.map((store) => (
        <React.Fragment key={store.branch}>
          <MarkerF
            position={store.coords}
            title={store.branch}
            icon={storeIcon(openStore === store.branch)}
            zIndex={2000}
            onClick={() =>
              setOpenStore((prev) =>
                prev === store.branch ? null : store.branch
              )
            }
          />
          {openStore === store.branch && (
            <CircleF
              center={store.coords}
              radius={radiusMiles * MILES_TO_METERS}
              options={{
                strokeColor: "#0284c7",
                strokeOpacity: 0.7,
                strokeWeight: 1,
                fillColor: "#0284c7",
                fillOpacity: 0.07,
                clickable: false,
              }}
            />
          )}
        </React.Fragment>
      ))}

      {view === "density" ? (
        density.cells.map((cell) => (
          <RectangleF
            key={cell.key}
            bounds={cell.bounds}
            options={{
              strokeColor: densityColor(cell.count),
              strokeOpacity: 0.9,
              strokeWeight: 1,
              fillColor: densityColor(cell.count),
              fillOpacity: 0.55,
            }}
            onClick={() => setSelectedBin(cell)}
          />
        ))
      ) : (
        <GoogleMarkerClusterer options={clusterOptions}>
          {(clusterer) => (
            <>
              {plotted.map((row) => (
                <MarkerF
                  key={row.lead._id}
                  position={row.coords}
                  clusterer={clusterer}
                  icon={pinIcon(row.state)}
                  title={row.lead.webleadEntry?.customerName || "Web lead"}
                  onClick={() => setSelected(row)}
                />
              ))}
            </>
          )}
        </GoogleMarkerClusterer>
      )}

      {selected && selected.coords && (
        <InfoWindowF
          position={selected.coords}
          onCloseClick={() => setSelected(null)}
        >
          <LeadPopup row={selected} />
        </InfoWindowF>
      )}

      {view === "density" && selectedBin && (
        <InfoWindowF
          position={{
            lat: (selectedBin.bounds.north + selectedBin.bounds.south) / 2,
            lng: (selectedBin.bounds.east + selectedBin.bounds.west) / 2,
          }}
          onCloseClick={() => setSelectedBin(null)}
        >
          <div className="text-slate-800">
            <p className="text-base font-extrabold">
              {selectedBin.count} {selectedBin.count === 1 ? "lead" : "leads"}
            </p>
            <p className="text-xs text-slate-500">in this area</p>
          </div>
        </InfoWindowF>
      )}
    </GoogleMap>
  );

  // Wall display: counts and a legend over a full-bleed map, nothing to click.
  if (display) {
    return (
      <div className="relative h-screen w-full overflow-hidden">
        {mapCanvas}
        <div className="pointer-events-none absolute inset-x-0 top-0 flex flex-wrap items-center gap-3 bg-gradient-to-b from-black/60 to-transparent p-5">
          <div>
            <h1 className="text-2xl font-extrabold tracking-tight text-white drop-shadow">
              Big C Web Leads
            </h1>
            <p className="text-sm font-semibold text-white/80 drop-shadow">
              {plotted.length} leads on the map · last{" "}
              {months === 1 ? "30 days" : `${months} months`}
              {loadedAt &&
                ` · updated ${loadedAt.toLocaleTimeString([], {
                  hour: "numeric",
                  minute: "2-digit",
                })}`}
            </p>
          </div>
        </div>
        <div className="pointer-events-none absolute inset-x-0 bottom-0 flex justify-center p-5">
          <Legend counts={stateCounts} activeStates={states} compact />
        </div>
      </div>
    );
  }

  return (
    <div className="app-canvas min-h-[calc(100vh-140px)] w-full">
      <PageShell size="2xl">
        <Link
          to="/webleads"
          className="app-muted -mt-1 mb-3 inline-flex items-center gap-1 text-xs font-semibold transition-colors hover:text-[color:var(--app-accent)]"
        >
          <ArrowBackIcon sx={{ fontSize: 14 }} />
          Web Leads
        </Link>
        <PageHeader
          icon={MapIcon}
          tone="emerald"
          eyebrow="Sales & Leads"
          title="Web Lead Map"
          description={
            <>
              <span className="tabular-nums">
                {plotted.length} of {visible.length}
              </span>{" "}
              matching leads plotted
              {unmapped > 0 && (
                <span title="Leads are geocoded when a manager is assigned.">
                  {" "}
                  · {unmapped} without a mapped address
                </span>
              )}
            </>
          }
          actions={
            <>
              <SegmentedControl
                label="Map view"
                value={view}
                onChange={(next) => {
                  setView(next);
                  setSelected(null);
                  setSelectedBin(null);
                  // Pin-mode zooms were ignored, so resync before binning.
                  if (next === "density" && mapRef.current)
                    setZoom(mapRef.current.getZoom() || 8);
                }}
                options={[
                  { value: "pins", label: "Pins" },
                  { value: "density", label: "Density" },
                ]}
              />
              <button
                type="button"
                onClick={load}
                disabled={loading}
                className="app-btn app-btn-secondary"
              >
                <RefreshIcon
                  sx={{ fontSize: 18 }}
                  className={loading ? "animate-spin" : ""}
                />
                Refresh
              </button>
            </>
          }
        />

        {error && (
          <p className="mb-4 rounded-xl bg-rose-500/10 px-3 py-2 text-sm font-medium text-rose-700 dark:text-rose-300">
            {error}
          </p>
        )}

        {/* ------------------------------------------------------- controls */}
        <div className="app-card mb-4 space-y-4 rounded-2xl border p-4">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative min-w-[220px] flex-1">
              <SearchIcon
                fontSize="small"
                className="app-muted pointer-events-none absolute left-3 top-1/2 -translate-y-1/2"
              />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search name, address, email or phone…"
                aria-label="Search leads on the map"
                className="app-input h-10 w-full rounded-xl border pl-9 pr-10 text-sm"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery("")}
                  className="app-icon-btn absolute right-0.5 top-1/2 h-9 w-9 -translate-y-1/2"
                  title="Clear search"
                  aria-label="Clear search"
                >
                  <CloseIcon fontSize="small" />
                </button>
              )}
              {searchResults.length > 0 && (
                <div className="app-glass absolute z-30 mt-1.5 w-full overflow-hidden rounded-xl border">
                  {searchResults.map((row) => (
                    <button
                      type="button"
                      key={row.lead._id}
                      onClick={() => focusLead(row)}
                      className="app-divider flex w-full items-center gap-2 border-b px-3 py-2.5 text-left text-xs transition-colors last:border-b-0 hover:bg-[color:var(--app-hover)]"
                    >
                      <span
                        className="h-2.5 w-2.5 shrink-0 rounded-full"
                        style={{ backgroundColor: PIN_STATES[row.state].color }}
                      />
                      <span className="shrink-0 font-semibold">
                        {row.lead.webleadEntry?.customerName || "Unknown"}
                      </span>
                      <span className="app-muted truncate">
                        {fullAddress(row.lead.webleadEntry)}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {branchOptions.length > 1 && (
              <select
                value={branch}
                onChange={(e) => setBranch(e.target.value)}
                aria-label="Branch"
                className="app-input h-10 rounded-xl border px-3 text-sm font-medium"
              >
                {canPickAnyBranch && (
                  <option value="All Locations">All Locations</option>
                )}
                {branchOptions.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            )}

            <SegmentedControl
              label="Date range"
              value={months}
              onChange={setMonths}
              options={RANGES.map((range) => ({
                value: range.months,
                label: range.label,
              }))}
            />

            <button
              type="button"
              onClick={resetFilters}
              className="app-btn app-btn-ghost app-btn-sm app-muted"
            >
              Reset
            </button>
          </div>

          <div>
            <p className="app-eyebrow mb-2">
              Status — click to show or hide
            </p>
            <Legend
              counts={stateCounts}
              activeStates={states}
              onToggle={(key) => toggleIn(states, setStates, key)}
            />
          </div>

          <div className="app-divider flex flex-wrap items-end gap-x-6 gap-y-3 border-t pt-4">
            <div className="min-w-0">
              <p className="app-eyebrow mb-2">
                Working on
              </p>
              <div className="flex flex-wrap gap-1.5">
                {CATEGORIES.map((category) => (
                  <Chip
                    key={category}
                    label={category}
                    active={categories.includes(category)}
                    onClick={() =>
                      toggleIn(categories, setCategories, category)
                    }
                  />
                ))}
              </div>
            </div>
            <div className="sm:ml-auto">
              <p className="app-eyebrow mb-2">
                Store radius
              </p>
              <SegmentedControl
                label="Store radius"
                value={radiusMiles}
                onChange={setRadiusMiles}
                options={RADII.map((radius) => ({
                  value: radius.miles,
                  label: radius.label,
                }))}
              />
            </div>
          </div>
        </div>

        <div className="app-card overflow-hidden rounded-2xl border">
          {mapCanvas}
        </div>
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
          <p className="app-muted text-xs">
            Click a store pin to draw its {radiusMiles}-mile ring.
            {view === "density" &&
              " Cells resize as you zoom; click one for its count."}
          </p>
          {view === "density" && density.max > 0 && (
            <div className="flex items-center gap-1.5">
              <span className="app-eyebrow">
                Leads per cell
              </span>
              <span className="app-muted text-[11px] font-semibold">1</span>
              <span className="app-divider flex overflow-hidden rounded-md border">
                {DENSITY_RAMP.map((color) => (
                  <span
                    key={color}
                    className="h-3 w-6"
                    style={{ backgroundColor: color }}
                  />
                ))}
              </span>
              <span className="app-muted text-[11px] font-semibold tabular-nums">
                {density.max}
              </span>
            </div>
          )}
        </div>
      </PageShell>
    </div>
  );
};

// InfoWindow content renders inside Google's own white bubble, so it keeps
// explicit slate text instead of the app surface classes.
const LeadPopup = ({ row }) => {
  const entry = row.lead.webleadEntry || {};
  const sale = row.lead.saleData || {};
  const manager = row.lead.assignInfo?.assignedManager;
  const sales = row.lead.assignInfo?.assignedSales || [];
  const state = PIN_STATES[row.state];

  return (
    <div className="min-w-[220px] max-w-[280px] text-slate-800">
      <div className="mb-1 flex items-center gap-1.5">
        <span
          className="h-2.5 w-2.5 shrink-0 rounded-full"
          style={{ backgroundColor: state.color }}
        />
        <span className="text-[11px] font-bold uppercase tracking-wide text-slate-500">
          {state.label}
        </span>
      </div>
      <p className="text-sm font-extrabold">
        {entry.customerName || "Unknown customer"}
      </p>
      {entry.customerWorkingOn && (
        <p className="text-xs text-slate-500">{entry.customerWorkingOn}</p>
      )}
      {fullAddress(entry) && (
        <a
          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
            fullAddress(entry)
          )}`}
          target="_blank"
          rel="noreferrer"
          className="mt-1 block text-xs text-sky-700 underline"
        >
          {fullAddress(entry)}
        </a>
      )}
      <div className="mt-1.5 flex flex-col gap-0.5 text-xs">
        {entry.customerPhone && (
          <a href={`tel:${entry.customerPhone}`} className="text-sky-700">
            {entry.customerPhone}
          </a>
        )}
        {entry.customerEmail && (
          <a
            href={`mailto:${entry.customerEmail}`}
            className="truncate text-sky-700"
          >
            {entry.customerEmail}
          </a>
        )}
      </div>
      <dl className="mt-2 border-t border-slate-200 pt-1.5 text-xs">
        <div className="flex justify-between gap-3">
          <dt className="text-slate-500">Manager</dt>
          <dd className="font-semibold">
            {manager?.Name ? titleCase(manager.Name) : "Unassigned"}
          </dd>
        </div>
        <div className="flex justify-between gap-3">
          <dt className="text-slate-500">Sales</dt>
          <dd className="truncate font-semibold">
            {sales.length
              ? sales.map((s) => titleCase(s?.Name || "")).join(", ")
              : "—"}
          </dd>
        </div>
        {(sale.quoteNumber || sale.orderNumber) && (
          <div className="flex justify-between gap-3">
            <dt className="text-slate-500">
              {sale.orderNumber ? "Order" : "Quote"}
            </dt>
            <dd className="font-semibold">
              {sale.orderNumber || sale.quoteNumber}
            </dd>
          </div>
        )}
        <div className="flex justify-between gap-3">
          <dt className="text-slate-500">Received</dt>
          <dd className="font-semibold">
            {entry.entryDate
              ? new Date(entry.entryDate).toLocaleDateString("en-US")
              : "—"}
          </dd>
        </div>
      </dl>
    </div>
  );
};

export default LeadMap;
